/** Shared start/end date rules for operational, financial, and other reports. */

export const todayIso = () => new Date().toISOString().slice(0, 10);

export const shiftIsoDate = (isoDate, dayOffset) => {
  const date = new Date(`${isoDate}T00:00:00`);
  date.setDate(date.getDate() + dayOffset);
  return date.toISOString().slice(0, 10);
};

export const inclusiveDayCount = (startDate, endDate) => {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  return Math.round((end - start) / 86400000) + 1;
};

/**
 * Calendar bounds for native date inputs.
 * Start date cannot be after end date; end date cannot be before start date.
 */
export const getReportDateRangeBounds = ({
  startDate = '',
  endDate = '',
  maxDateRangeDays = null,
  today = todayIso(),
} = {}) => {
  let startMax = today;
  let startMin = '';
  let endMax = today;
  let endMin = startDate || '';

  if (endDate) {
    startMax = endDate < today ? endDate : today;
  }

  if (maxDateRangeDays && endDate) {
    startMin = shiftIsoDate(endDate, -(maxDateRangeDays - 1));
  }

  if (maxDateRangeDays && startDate) {
    const windowMax = shiftIsoDate(startDate, maxDateRangeDays - 1);
    endMax = windowMax < today ? windowMax : today;
  }

  return { startMin, startMax, endMin, endMax };
};

export const applyReportStartDateChange = (current, startDate, options = {}) => {
  const today = options.today || todayIso();
  const maxDateRangeDays = options.maxDateRangeDays || null;
  let start_date = startDate || '';
  let end_date = current.end_date || '';

  if (start_date && start_date > today) {
    start_date = today;
  }

  if (start_date && end_date) {
    if (end_date < start_date) {
      end_date = start_date;
    } else if (maxDateRangeDays) {
      const maxEnd = shiftIsoDate(start_date, maxDateRangeDays - 1);
      const cappedMax = maxEnd < today ? maxEnd : today;
      if (end_date > cappedMax) {
        end_date = cappedMax;
      }
    }
  }

  return { start_date, end_date };
};

/** When end date moves earlier, pull start date forward to match if needed. */
export const applyReportEndDateChange = (current, endDate, options = {}) => {
  const today = options.today || todayIso();
  const maxDateRangeDays = options.maxDateRangeDays || null;
  let end_date = endDate || '';
  let start_date = current.start_date || '';

  if (end_date && end_date > today) {
    end_date = today;
  }

  if (end_date) {
    if (!start_date || start_date > end_date) {
      start_date = end_date;
    } else if (maxDateRangeDays && inclusiveDayCount(start_date, end_date) > maxDateRangeDays) {
      start_date = shiftIsoDate(end_date, -(maxDateRangeDays - 1));
    }
  }

  return { start_date, end_date };
};

export const validateReportDateRange = (values, options = {}) => {
  const { start_date, end_date } = values;
  const maxDateRangeDays = options.maxDateRangeDays || null;

  if (!start_date || !end_date) {
    return options.requiredMessage || 'Please select start and end dates';
  }
  if (end_date < start_date) {
    return 'End date must be on or after start date';
  }
  if (maxDateRangeDays && inclusiveDayCount(start_date, end_date) > maxDateRangeDays) {
    return `Date range cannot exceed ${maxDateRangeDays} days.`;
  }
  return null;
};

/** For legacy report forms that use a single handleInput on named date fields. */
export const handleReportDateRangeInput = (event, currentValues, setValues, options = {}) => {
  const { name, value } = event.target;
  if (name !== 'start_date' && name !== 'end_date') {
    setValues({ ...currentValues, [name]: value });
    return;
  }

  const next = name === 'start_date'
    ? applyReportStartDateChange(currentValues, value, options)
    : applyReportEndDateChange(currentValues, value, options);

  setValues({ ...currentValues, ...next });
};
